/**
 * Microsoft Purview through the AIOE (phases 2 and 3 of
 * docs/plans/2026-10-purview-sensitivity-labels.md; the contract is
 * docs/plans/2026-10-agent-365-aioe-contract.md, "Purview labels", P-§1…).
 *
 * The AIOE is the only piece that holds a Microsoft Graph credential. A
 * workbench or node host asks it, with its device token, for:
 *
 *   resolve-label       one label id the policy's taxonomy does not list
 *                       (newer than the last sync, or another tenant's) →
 *                       its name and the sensitivity the organisation maps
 *                       it to (P-§2);
 *   protection-scopes   whether Purview wants this person's prompts to this
 *                       app evaluated inline, with an ETag (P-§3);
 *   process-content     Purview's verdict on one prompt or request, for the
 *                       person (P-§4).
 *
 * And the `dlp.purview` policy clause that turns the inline verdict on.
 * Mirror this file in mju-aioe `packages/shared/src/schemas/purview.ts`
 * (identical but for this comment) and the clause under `dlp.purview` in
 * its `policy.ts`.
 */
import { z } from "zod";
import { sensitivitySchema } from "./sensitivityLabels.js";
/** The relay routes, relative to the organisation's API base. */
export const PURVIEW_ROUTES = {
    resolveLabel: "/m365/v1/purview/resolve-label",
    protectionScopes: "/m365/v1/purview/protection-scopes",
    processContent: "/m365/v1/purview/process-content",
};
// ── The policy clause: `dlp.purview` (P-§5) ───────────────────────────
/**
 * The inline Purview DLP verdict, off unless the policy turns it on.
 *
 *   evaluate     `prompts`: what a person sends from the composer; `all`:
 *                that and every proxied request an agent makes to a hosted
 *                provider. `off` (the default): nothing is asked.
 *   classifier   `also` (the default): Purview's verdict first, then the
 *                local classifier as before; `instead`: a verdict Purview
 *                gave stands in for the classifier, which runs only when
 *                Purview could not answer.
 *   failClosed   when Purview cannot answer in `budgetMs` (or at all): stop
 *                the prompt (true) or warn and let the local chain decide
 *                (false, the default).
 *   budgetMs     how long one verdict may take (default 4 s).
 */
export const purviewPolicySchema = z.object({
    evaluate: z.enum(["off", "prompts", "all"]).default("off"),
    classifier: z.enum(["also", "instead"]).default("also"),
    failClosed: z.boolean().default(false),
    budgetMs: z.number().int().min(250).max(30_000).default(4_000),
});
// ── resolve-label (P-§2) ──────────────────────────────────────────────
const guid = z.string().min(1).max(64);
export const resolveLabelRequestSchema = z.object({
    /** The label's GUID as the file carries it (braces stripped, lower case). */
    labelId: guid,
    /** The tenant the file says the label is from (`MSIP_Label_<id>_SiteId`), when it says. */
    siteId: guid.optional(),
    /** The label's name as the file carries it (`_Name`), a hint only. */
    name: z.string().max(200).optional(),
    projectId: z.string().max(200).optional(),
    rootId: z.string().max(200).optional(),
});
export const resolveLabelAnswerSchema = z.object({
    labelId: guid,
    /** The organisation knows the label (its own, or a partner's it mapped). False: the workbench's fallback applies. */
    known: z.boolean(),
    name: z.string().min(1).max(200).optional(),
    sensitivity: sensitivitySchema.optional(),
    parentId: guid.nullish(),
    hasProtection: z.boolean().optional(),
    /** The tenant the label belongs to. */
    tenantId: z.string().max(64).optional(),
    /** When the AIOE read it from the tenant. */
    fetchedAt: z.string().max(40).optional(),
});
// ── protection-scopes (P-§3) ──────────────────────────────────────────
export const PURVIEW_ACTIVITIES = ["uploadText", "downloadText"];
export const purviewActivitySchema = z.enum(PURVIEW_ACTIVITIES);
export const protectionScopesRequestSchema = z.object({
    activities: z.array(purviewActivitySchema).min(1).max(2),
    projectId: z.string().max(200).optional(),
});
export const protectionScopesAnswerSchema = z.object({
    /** The scope state's ETag: sent back as `scopeEtag` on process-content, and as `If-None-Match` here. */
    etag: z.string().min(1).max(200),
    /**
     * Whether this person's prompts to the workbench are in scope of a Purview DLP policy, and how
     * Purview wants them: `evaluateInline` (ask process-content before sending), `evaluateOffline`
     * (the AIOE reports them afterwards; nothing waits), or not at all (an empty list).
     */
    scopes: z
        .array(z.object({
        activities: z.array(purviewActivitySchema).max(2),
        executionMode: z.enum(["evaluateInline", "evaluateOffline"]),
    }))
        .max(50),
    /** Seconds the answer may be kept before asking again (default 300). */
    maxAgeSeconds: z.number().int().min(0).max(86_400).optional(),
});
// ── process-content (P-§4) ────────────────────────────────────────────
export const MAX_PROCESS_CONTENT_CHARS = 200_000;
export const processContentRequestSchema = z.object({
    activity: purviewActivitySchema,
    /** SHA-256 (hex) of the texts joined with "\n\n": the AIOE may answer a digest it has seen from its cache. */
    digest: z.string().regex(/^[0-9a-f]{64}$/),
    /** The texts, already through masking and pattern redaction. Never sent for content a label keeps on the machine. */
    content: z
        .array(z.object({ id: z.string().min(1).max(100), text: z.string().max(MAX_PROCESS_CONTENT_CHARS) }))
        .min(1)
        .max(50),
    /** `prompt` (the composer) or `proxy` (an agent's request through the workbench's proxy). */
    where: z.enum(["prompt", "proxy"]),
    /** The provider the content is bound for (`anthropic`, `copilot` …) and its route class. */
    provider: z.string().max(100).optional(),
    route: z.enum(["organisation", "hosted"]).optional(),
    sessionId: z.string().max(200).optional(),
    /** The person the session acts for, when the host knows (the AIOE otherwise uses the device's person). */
    principal: z.string().max(200).optional(),
    /** The last protection-scopes ETag this host holds. */
    scopeEtag: z.string().max(200).optional(),
    projectId: z.string().max(200).optional(),
    rootId: z.string().max(200).optional(),
});
export const processContentAnswerSchema = z.object({
    /**
     * The verdict, folded from Purview's `policyActions`: `block` (a `restrictAccess` with
     * `restrictionAction: block`), `warn` (`restrictionAction: warn`), else `allow`.
     */
    action: z.enum(["allow", "warn", "block"]),
    /** What a person reads: the policy's own tip when Purview gave one. */
    reason: z.string().max(1000).optional(),
    /** The Purview policies that matched, by name, for the audit row. */
    policies: z.array(z.string().max(200)).max(20).optional(),
    /** `modified`: the scope state changed since `scopeEtag` — ask protection-scopes again. */
    protectionScopeState: z.enum(["notModified", "modified"]).optional(),
    /**
     * False when Purview did not evaluate — the AIOE answers so when Purview reported processing errors
     * (from 0.5.8, the error as `reason`). The workbench treats it as Purview unavailable: `failClosed`
     * blocks, otherwise the fallback decides; never an allow.
     */
    evaluated: z.boolean().optional(),
});
//# sourceMappingURL=purview.js.map